import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useCan } from "@/context/AuthContext";
import { todayIso } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Chips = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button key={o.id} type="button" data-testid={`${testId}-${o.id}`} onClick={() => onChange(value.includes(o.id) ? value.filter((x) => x !== o.id) : [...value, o.id])}
        className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${value.includes(o.id) ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground"}`}>{o.name}</button>
    ))}
  </div>
);

const Preferences = () => {
  const [p, setP] = useState(null);
  const [setup, setSetup] = useState({ areas: [], templates: [] });
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    api.get("/members/me").then((r) => setP(r.data));
    api.get("/rota/setup").then((r) => setSetup(r.data));
    api.get("/bikes").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);
  if (!p) return null;
  const positions = [...new Map(setup.templates.flatMap((t) => t.requirements).map((r) => [r.key, { id: r.key, name: r.label }])).values()];
  const save = async () => {
    try { await api.patch("/members/me/preferences", p); toast.success("Preferences saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-4 rounded-2xl border bg-card p-5" data-testid="my-preferences">
      <h3 className="font-bold">My rota preferences</h3>
      <p className="text-xs text-muted-foreground">Leave a section empty to allow everything.</p>
      <div className="space-y-2"><Label>Areas I cover</Label><Chips options={setup.areas.filter((a) => a.active)} value={p.areas} onChange={(v) => setP({ ...p, areas: v })} testId="pref-area" /></div>
      <div className="space-y-2"><Label>Roles I do</Label><Chips options={positions} value={p.positions} onChange={(v) => setP({ ...p, positions: v })} testId="pref-position" /></div>
      <div className="space-y-2"><Label>Vehicles I can use</Label><Chips options={vehicles} value={p.vehicles} onChange={(v) => setP({ ...p, vehicles: v })} testId="pref-vehicle" /></div>
      <div className="max-w-xs space-y-1.5"><Label>Phone (shown to controllers)</Label><Input value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} data-testid="pref-phone" /></div>
      <button onClick={save} data-testid="pref-save-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Save preferences</button>
    </section>
  );
};

const Row = ({ a, onDelete, showName }) => (
  <div data-testid={`away-row-${a.id}`} className="flex items-center justify-between gap-3 border-b px-5 py-3 text-sm last:border-0">
    <span>{showName && <b className="mr-2">{a.user_name}</b>}{fmtDate(a.start_date)} – {fmtDate(a.end_date)} {a.note && <span className="text-muted-foreground">· {a.note}</span>}</span>
    {onDelete && <button onClick={() => onDelete(a)} data-testid={`away-delete-${a.id}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
  </div>
);

export default function Availability() {
  const can = useCan();
  const team = can("rota", 3) || can("control", 1);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [f, setF] = useState({ start_date: todayIso(), end_date: todayIso(), note: "" });

  const load = () => {
    api.get("/availability", { params: { mine: true, start: todayIso() } }).then((r) => setMine(r.data));
    if (team) api.get("/availability", { params: { start: todayIso() } }).then((r) => setAll(r.data));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/availability", { ...f, kind: "unavailable" });
      toast.success(data.removed_shifts?.length ? `Saved. Removed you from: ${data.removed_shifts.join(", ")}` : "Saved — you won't be rostered on these dates");
      setF({ ...f, note: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (a) => { try { await api.delete(`/availability/${a.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Holidays</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">My availability</h1>
      </div>
      <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-2xl border bg-card p-5" data-testid="away-form">
        <div className="space-y-1.5"><Label>Away from</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="away-start" /></div>
        <div className="space-y-1.5"><Label>Until</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="away-end" /></div>
        <div className="min-w-[180px] flex-1 space-y-1.5"><Label>Note</Label><Input placeholder="e.g. Holiday" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="away-note" /></div>
        <button type="submit" data-testid="away-add-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Mark unavailable</button>
      </form>
      <section className="overflow-hidden rounded-2xl border bg-card" data-testid="my-away-list">
        <h3 className="border-b px-5 py-3 font-bold">My upcoming time away</h3>
        {mine.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">You're available for every date.</p> : mine.map((a) => <Row key={a.id} a={a} onDelete={del} />)}
      </section>
      {team && (
        <section className="overflow-hidden rounded-2xl border bg-card" data-testid="team-away-list">
          <h3 className="border-b px-5 py-3 font-bold">Team availability (upcoming)</h3>
          {all.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">Nobody away.</p> : all.map((a) => <Row key={a.id} a={a} showName onDelete={can("rota", 3) ? del : null} />)}
        </section>
      )}
      <Preferences />
    </div>
  );
}
